// Service card for the Shop · Services list. Different shape from product card:
// shows partner badge, duration tiers and a "Book" capsule instead of "Buy".
import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import CardCapsule from '../ui/CardCapsule';

const fmtINR = (n) =>
  n == null
    ? null
    : new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      }).format(n);

export default function ServiceCard({ service, index }) {
  const navigate = useNavigate();
  const variants = service.variants || [];
  const minPrice = service.base_price;
  const isOnRequest = service.combo_only || minPrice == null || minPrice === 0;
  // "60/90 min" — distinct durations, shortest first.
  const durations = [...new Set(variants.map((v) => v.duration_minutes).filter(Boolean))]
    .sort((x, y) => x - y)
    .slice(0, 3);
  const meta = [service.service_category, durations.length ? `${durations.join('/')} min` : null]
    .filter(Boolean);

  return (
    <div
      data-testid={`service-card-${index}`}
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/shop/services/${service.id}`)}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/shop/services/${service.id}`); }}
      className="bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100 cursor-pointer transition-transform active:scale-[0.98]"
    >
      <div className="relative h-36 bg-[#2B3A67]/5">
        {service.images?.[0] && (
          <img
            data-testid={`service-image-${service.id}`}
            src={service.images[0]}
            alt={service.name}
            className="w-full h-36 object-cover"
            referrerPolicy="no-referrer"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
        {service.partner_brand && (
          <span
            data-testid={`service-partner-${service.id}`}
            className="absolute top-2 left-2 bg-[#2B3A67] text-white text-[10px] font-black px-2 py-1 rounded-full uppercase tracking-wider"
          >
            {service.partner_brand}
          </span>
        )}
        {isOnRequest && (
          <span
            data-testid={`service-on-request-${service.id}`}
            className="absolute top-2 right-2 bg-[#F4D341] text-[#2B3A67] text-[10px] font-black px-2 py-1 rounded-full"
          >
            ON REQUEST
          </span>
        )}
        <CardCapsule label={isOnRequest ? 'Enquire' : 'Book'} testId={`service-book-${service.id}`} />
      </div>

      <div className="p-4">
        <h3
          data-testid={`service-name-${service.id}`}
          className="font-bold text-sm leading-snug line-clamp-2"
        >
          {service.name}
        </h3>
        {/* Each part stays whole; the line wraps between them only when needed. */}
        {meta.length > 0 && (
          <p className="text-xs text-gray-500 mt-0.5 leading-snug">
            {meta.map((part, i) => (
              <Fragment key={part}>
                {i > 0 && ' · '}
                <span className="whitespace-nowrap">{part}</span>
              </Fragment>
            ))}
          </p>
        )}
        <p className="mt-2 leading-none" data-testid={`service-price-${service.id}`}>
          {isOnRequest ? (
            <span className="text-xs font-bold text-gray-400">Price on request</span>
          ) : (
            <>
              <span className="text-[10px] font-bold text-gray-400">from </span>
              <span className="font-black text-[#2B3A67]">{fmtINR(minPrice)}</span>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
